import { useQuery } from '@tanstack/react-query';
import { transactionService } from '@services/transactionService';
import { recurrenceService } from '@services/recurrenceService';
import { installmentService } from '@services/installmentService';
import { accountService } from '@services/accountService';

/**
 * Hook unificado que combina transações, recorrências e parcelamentos
 * para criar uma visão completa do fluxo de caixa
 */
export const useCashFlow = (filters = {}) => {
  const { startDate, endDate, accountId, categoryId, type } = filters;

  // Buscar transações desde o início para calcular saldo corretamente
  const balanceStartDate = '2020-01-01';

  const transactionsQuery = useQuery({
    queryKey: ['transactions', 'cashflow', balanceStartDate, endDate, accountId, categoryId, type],
    queryFn: async () => {
      const allTransactions = [];
      let lastKey = null;
      let hasMore = true;

      // Buscar todas as páginas
      while (hasMore) {
        const result = await transactionService.getAll({
          startDate: balanceStartDate,
          endDate,
          accountId,
          categoryId,
          type,
          limit: 100, // Respeitar limite do backend
          lastKey,
        });

        const transactions = Array.isArray(result) 
          ? result 
          : (result?.transactions || []);
        
        allTransactions.push(...transactions);
        
        lastKey = result?.lastKey;
        hasMore = !!lastKey;
        
        // Segurança: máximo 10 páginas (1000 transações)
        if (allTransactions.length >= 1000) break;
      }

      return allTransactions;
    },
    enabled: !!startDate && !!endDate,
  });

  // Buscar recorrências
  const recurrencesQuery = useQuery({
    queryKey: ['recurrences'],
    queryFn: recurrenceService.getAll,
  });

  // Buscar parcelamentos
  const installmentsQuery = useQuery({
    queryKey: ['installments'],
    queryFn: () => installmentService.getAll(false),
  });

  // Buscar contas para saldo inicial
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: accountService.getAll,
  });

  const isLoading = transactionsQuery.isLoading || 
    recurrencesQuery.isLoading || 
    installmentsQuery.isLoading ||
    accountsQuery.isLoading;

  // Processar e combinar dados
  const processData = () => {
    if (!startDate || !endDate) return { items: [], dailyBalances: [], summary: {} };

    const transactions = transactionsQuery.data || [];
    const recurrences = recurrencesQuery.data || [];
    const installments = installmentsQuery.data || [];
    const accounts = accountsQuery.data || [];

    // Criar mapa de itens por data
    const itemsByDate = new Map();

    // Adicionar transações realizadas
    transactions.forEach(t => {
      const date = t.date;
      if (!itemsByDate.has(date)) {
        itemsByDate.set(date, []);
      }
      itemsByDate.get(date).push({
        ...t,
        source: 'transaction',
        status: 'completed',
        id: t.transactionId,
      });
    });

    // Gerar ocorrências de recorrências no período
    recurrences.forEach(r => {
      if (!r.isActive) return;
      
      // Aplicar filtros
      if (accountId && r.accountId !== accountId) return;
      if (categoryId && r.categoryId !== categoryId) return;
      if (type && r.type !== type) return;

      const occurrences = generateRecurrenceOccurrences(r, startDate, endDate);
      occurrences.forEach(occ => {
        // Verificar se já existe transação para essa recorrência nessa data
        const existingTransaction = transactions.find(
          t => t.recurringId === r.recurrenceId && t.date === occ.date
        );
        
        if (!existingTransaction) {
          if (!itemsByDate.has(occ.date)) {
            itemsByDate.set(occ.date, []);
          }
          itemsByDate.get(occ.date).push({
            ...r,
            ...occ,
            source: 'recurrence',
            status: 'pending',
            id: `${r.recurrenceId}-${occ.date}`,
            transactionId: null,
          });
        }
      });
    });

    // Adicionar parcelas pendentes no período
    installments.forEach(inst => {
      if (!inst.isActive || inst.isCompleted) return;

      // Aplicar filtros
      if (accountId && inst.accountId !== accountId) return;
      if (categoryId && inst.categoryId !== categoryId) return;
      if (type && type !== 'expense') return;

      inst.schedule?.forEach(s => {
        if (s.isPaid) return;
        if (s.dueDate < startDate || s.dueDate > endDate) return;

        if (!itemsByDate.has(s.dueDate)) {
          itemsByDate.set(s.dueDate, []);
        }
        itemsByDate.get(s.dueDate).push({
          transactionId: null,
          description: `${inst.description} (${s.number}/${inst.totalInstallments})`,
          amount: -s.amount,
          type: 'expense',
          date: s.dueDate,
          accountId: inst.accountId,
          categoryId: inst.categoryId,
          source: 'installment',
          status: 'pending',
          id: `${inst.installmentId}-${s.number}`,
          installmentId: inst.installmentId,
          installmentNumber: s.number,
          totalInstallments: inst.totalInstallments,
        });
      });
    });

    // Converter para array e ordenar por data
    const allItems = [];
    itemsByDate.forEach((items, date) => {
      items.forEach(item => allItems.push(item));
    });
    allItems.sort((a, b) => a.date.localeCompare(b.date));

    // Caixa NÃO inclui cartão de crédito (é dívida, não dinheiro em conta). Compra no
    // cartão não sai do caixa; só a fatura (transfer da conta -> cartão) reduz o caixa.
    // Se o usuário filtrou por um cartão específico, aí mostramos o saldo (dívida) dele.
    const creditCardIds = new Set(
      accounts.filter(a => a.type === 'credit_card').map(a => a.accountId)
    );
    const viewingCard = accountId && creditCardIds.has(accountId);
    const excludeCardIds = viewingCard ? new Set() : creditCardIds;

    // Calcular saldo diário a partir do saldo inicial das contas (não o atual)
    const initialBalance = accounts.reduce((sum, acc) => {
      if (accountId) return acc.accountId === accountId ? sum + (acc.initialBalance || 0) : sum;
      if (excludeCardIds.has(acc.accountId)) return sum;
      return sum + (acc.initialBalance || 0);
    }, 0);

    // Projeção da fatura do cartão como SAÍDA DE CAIXA no vencimento (só visão agregada e
    // só no saldo — não vira linha na lista). A 1ª fatura futura carrega todo o saldo devedor
    // acumulado até o fechamento; as próximas, só o incremental do ciclo. Compras feitas
    // depois do fechamento caem na fatura seguinte (por isso o closingDay).
    const faturaProjections = [];
    if (!accountId) {
      const todayStr = new Date().toISOString().split('T')[0];
      const [fy, fm] = todayStr.split('-').map(Number);
      accounts.filter(a => a.type === 'credit_card' && a.dueDay).forEach(card => {
        const closingDay = card.closingDay || Math.max(1, card.dueDay - 7);
        const cardTxns = transactions.filter(t => t.accountId === card.accountId);
        let prevClosing = null;
        for (let i = 0; i < 18; i++) {
          const dueStr = new Date(fy, fm - 1 + i, card.dueDay, 12).toISOString().split('T')[0];
          if (dueStr < todayStr) continue;
          if (dueStr > endDate) break;
          const [cy, cm] = dueStr.split('-').map(Number);
          const closingStr = new Date(cy, cm - 1, closingDay, 12).toISOString().split('T')[0];
          const amount = prevClosing === null
            ? (card.initialBalance || 0) + cardTxns.filter(t => t.date <= closingStr).reduce((s, t) => s + t.amount, 0)
            : cardTxns.filter(t => t.date > prevClosing && t.date <= closingStr).reduce((s, t) => s + t.amount, 0);
          prevClosing = closingStr;
          if (amount < 0) {
            faturaProjections.push({
              transactionId: null,
              description: `Fatura ${card.name} (prevista)`,
              amount,
              type: 'expense',
              date: dueStr,
              accountId: null,
              categoryId: 'fatura',
              source: 'card-fatura',
              status: 'pending',
              id: `fatura-${card.accountId}-${dueStr}`,
            });
          }
        }
      });
    }

    const dailyBalances = calculateDailyBalances([...allItems, ...faturaProjections], balanceStartDate, endDate, initialBalance, excludeCardIds);

    // Calcular resumo
    const summary = {
      totalIncome: allItems.filter(i => i.type === 'income').reduce((sum, i) => sum + Math.abs(i.amount), 0),
      totalExpense: allItems.filter(i => i.type === 'expense').reduce((sum, i) => sum + Math.abs(i.amount), 0),
      pendingIncome: allItems.filter(i => i.type === 'income' && i.status === 'pending').reduce((sum, i) => sum + Math.abs(i.amount), 0),
      pendingExpense: allItems.filter(i => i.type === 'expense' && i.status === 'pending').reduce((sum, i) => sum + Math.abs(i.amount), 0),
      completedIncome: allItems.filter(i => i.type === 'income' && i.status === 'completed').reduce((sum, i) => sum + Math.abs(i.amount), 0),
      completedExpense: allItems.filter(i => i.type === 'expense' && i.status === 'completed').reduce((sum, i) => sum + Math.abs(i.amount), 0),
    };

    return {
      items: allItems,
      dailyBalances,
      summary,
    };
  };

  const data = processData();

  return {
    items: data.items,
    dailyBalances: data.dailyBalances,
    summary: data.summary,
    isLoading,
    error: transactionsQuery.error || recurrencesQuery.error || installmentsQuery.error,
    refetch: () => Promise.all([
      transactionsQuery.refetch(),
      recurrencesQuery.refetch(),
      installmentsQuery.refetch(),
      accountsQuery.refetch(),
    ]),
  };
};

/**
 * Gera ocorrências de uma recorrência no período
 */
function generateRecurrenceOccurrences(recurrence, startDate, endDate) {
  const occurrences = [];
  const { frequency, dayOfMonth, startDate: recStartDate, endDate: recEndDate } = recurrence;
  // Recorrência é PREVISÃO: só projeta de hoje pra frente. Datas passadas já aconteceram
  // (viraram transação real no extrato) ou são história — não reprojetar (evita duplicar
  // o histórico importado e mostra "o que ainda vem no mês"). recStart corrige o bug de
  // gerar datas antes do início da recorrência.
  const todayStr = new Date().toISOString().split('T')[0];
  const recStart = recStartDate ? String(recStartDate).slice(0, 10) : '0000-00-00';

  let currentDate = new Date(Math.max(new Date(startDate), new Date(recStartDate)));
  const end = recEndDate ? new Date(Math.min(new Date(endDate), new Date(recEndDate))) : new Date(endDate);

  if (frequency === 'monthly' && dayOfMonth) {
    currentDate.setDate(dayOfMonth);
    if (currentDate < new Date(startDate)) {
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
  }

  const maxIterations = 100;
  let iterations = 0;

  while (currentDate <= end && iterations < maxIterations) {
    iterations++;
    const dateStr = currentDate.toISOString().split('T')[0];

    if (dateStr >= startDate && dateStr <= endDate && dateStr >= todayStr && dateStr >= recStart) {
      occurrences.push({
        date: dateStr,
        amount: recurrence.amount,
        type: recurrence.type,
        description: recurrence.description,
        accountId: recurrence.accountId,
        categoryId: recurrence.categoryId,
      });
    }

    switch (frequency) {
      case 'daily':
        currentDate.setDate(currentDate.getDate() + 1);
        break;
      case 'weekly':
        currentDate.setDate(currentDate.getDate() + 7);
        break;
      case 'biweekly':
        currentDate.setDate(currentDate.getDate() + 14);
        break;
      case 'monthly':
        currentDate.setMonth(currentDate.getMonth() + 1);
        if (dayOfMonth) currentDate.setDate(dayOfMonth);
        break;
      case 'yearly':
        currentDate.setFullYear(currentDate.getFullYear() + 1);
        break;
      default:
        currentDate.setMonth(currentDate.getMonth() + 1);
    }
  }

  return occurrences;
}

/**
 * Calcula saldo diário para cada dia do período
 */
function calculateDailyBalances(items, startDate, endDate, initialBalance, excludeCardIds = new Set()) {
  const balances = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  const itemsByDate = new Map();
  items.forEach(item => {
    if (!itemsByDate.has(item.date)) {
      itemsByDate.set(item.date, []);
    }
    itemsByDate.get(item.date).push(item);
  });

  let runningBalance = initialBalance;
  let currentDate = new Date(start);

  while (currentDate <= end) {
    const dateStr = currentDate.toISOString().split('T')[0];
    const dayItems = itemsByDate.get(dateStr) || [];

    // Caixa: ignora lançamentos do cartão (compra é dívida, não sai do caixa). A fatura
    // entra como transfer da conta -> cartão; essa perna (na conta) conta como saída.
    const cashItems = dayItems.filter(i => !excludeCardIds.has(i.accountId));
    const dayIncome = cashItems.filter(i => i.type === 'income').reduce((sum, i) => sum + Math.abs(i.amount), 0);
    const dayExpense = cashItems.filter(i => i.type === 'expense').reduce((sum, i) => sum + Math.abs(i.amount), 0);
    const dayTransfer = cashItems.filter(i => i.type === 'transfer').reduce((sum, i) => sum + i.amount, 0);

    if (cashItems.length > 0) {
      runningBalance += (dayIncome - dayExpense + dayTransfer);
    }

    balances.push({
      date: dateStr,
      balance: runningBalance,
      income: dayIncome,
      expense: dayExpense,
      transactions: dayItems.length,
      isNegative: runningBalance < 0,
    });

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return balances;
}

export default useCashFlow;
